/**
 * Fisheye dewarp (ENG-2761), staff only.
 *
 * Fisheye cameras need a fish2pano flag string in `Camera.panorama_parameters`
 * (the connector runs fish2pano with it). Staff used to type it by hand in
 * Django admin with no preview. Here they pick the camera, start from a
 * preset or the controls, see an approximate panorama drawn from the camera
 * test's raw frame (or a local image), and save. A save marks the site
 * unsynced: nothing reaches the connector until the next Sync.
 *
 * `Camera.dewarp_parameters` is not applied by the connector at all; when a
 * camera has one the tab says so and offers to copy it into the editor.
 */
import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  Card,
  CardHeader,
  EmptyCard,
  Hint,
  Mono,
  Pill,
  SampleChip,
  SectionLabel,
  TS_COLORS,
  TsButton,
} from "../TroubleshootingUi";
import {
  MONO,
  MULISH,
} from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useIsStaffUser } from "../../../utils/staffUser";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { copyToClipboard } from "../TroubleshootingUtils/buildDiagnosticReport";
import {
  fetchSiteCameras,
  SiteCamera,
} from "../TroubleshootingUtils/troubleshootingApi";
import {
  CameraPanorama,
  CameraSnapshot,
  fetchCameraPanorama,
  fetchCameraSnapshot,
  PanoramaOutcome,
  SAMPLE_CAMERA_ID,
  SAMPLE_PANORAMA,
  saveCameraPanorama,
} from "../TroubleshootingUtils/fisheyeApi";
import {
  dewarpFisheye,
  FIXED_TILT,
  fisheyeTestPattern,
  normalizePanoramaText,
  PANORAMA_PRESETS,
  PanoFlag,
  PanoParams,
  panoramaLayout,
  panoStyleOf,
  parsePanoramaParameters,
  Raster,
  serializePanoramaParameters,
  switchPanoStyle,
} from "../TroubleshootingUtils/fisheyeDewarp";

/** Preview is rendered at most this wide, then scaled to fit. */
const PREVIEW_MAX_WIDTH = 1600;
/** Frames are shrunk to this longest side before sampling. */
const SOURCE_MAX_SIDE = 2048;
const PREVIEW_DEBOUNCE_MS = 150;
const SAMPLE_FRAME_SIZE = 720;

export const PREVIEW_LABEL =
  "Approximate preview — horizontal layout matches the connector; vertical framing can differ from fish2pano (ENG-1724)";

interface Frame {
  raster: Raster;
  /** sampled size / full size; pixel flags (-c, -r) are in full-size pixels. */
  sourceScale: number;
  naturalWidth: number;
  naturalHeight: number;
}

type Source =
  | { kind: "snapshot"; url: string; snapshot: CameraSnapshot }
  | { kind: "file"; url: string; name: string }
  | { kind: "sample" };

/** Pixels of a loaded image, shrunk to SOURCE_MAX_SIDE. Throws when tainted. */
const frameFromImage = (img: HTMLImageElement): Frame | null => {
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  if (!naturalWidth || !naturalHeight) return null;
  const scale = Math.min(
    1,
    SOURCE_MAX_SIDE / Math.max(naturalWidth, naturalHeight)
  );
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);
  // SecurityError here when the image came without CORS headers.
  const pixels = ctx.getImageData(0, 0, width, height);
  return {
    raster: { width, height, data: pixels.data },
    sourceScale: scale,
    naturalWidth,
    naturalHeight,
  };
};

const drawRaster = (canvas: HTMLCanvasElement | null, raster: Raster) => {
  if (!canvas) return;
  canvas.width = raster.width;
  canvas.height = raster.height;
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext("2d");
  } catch {
    ctx = null;
  }
  if (!ctx) return;
  const image = ctx.createImageData(raster.width, raster.height);
  image.data.set(raster.data);
  ctx.putImageData(image, 0, 0);
};

const NUMBER_DRAFT = /^-?(\d+(\.\d*)?|\.\d+)$/;

/**
 * A number field that keeps what is being typed ("-", "12.") until it reads
 * as a number, then commits it. Empty commits `undefined` (drop the flag).
 */
const NumField = ({
  label,
  flag,
  value,
  onCommit,
  disabled,
  testId,
}: {
  label: string;
  flag: string;
  value: number | undefined;
  onCommit: (value: number | undefined) => void;
  disabled?: boolean;
  testId?: string;
}) => {
  const [draft, setDraft] = React.useState(value == null ? "" : String(value));
  React.useEffect(() => {
    setDraft((current) =>
      value == null
        ? current === ""
          ? current
          : ""
        : Number(current) === value && NUMBER_DRAFT.test(current)
        ? current
        : String(value)
    );
  }, [value]);
  const bad = draft !== "" && !NUMBER_DRAFT.test(draft);
  return (
    <TextField
      size="small"
      label={
        <span>
          {label} <span style={{ ...MONO, fontSize: 11 }}>{flag}</span>
        </span>
      }
      value={draft}
      disabled={disabled}
      error={bad}
      inputProps={{ inputMode: "decimal", "data-testid": testId }}
      onChange={(event) => {
        const next = event.target.value.trim();
        setDraft(next);
        if (next === "") onCommit(undefined);
        else if (NUMBER_DRAFT.test(next)) onCommit(Number(next));
      }}
      sx={{
        width: 150,
        "& .MuiInputBase-root": { ...MULISH, backgroundColor: "#fff" },
      }}
    />
  );
};

const describeSnapshotAge = (snapshot: CameraSnapshot): string => {
  if (!snapshot.checkedAt) return "From the latest camera test.";
  const at = new Date(snapshot.checkedAt);
  return Number.isNaN(at.getTime())
    ? "From the latest camera test."
    : `From the camera test of ${at.toLocaleString()}.`;
};

export const FisheyeDewarpTab = () => {
  const staff = useIsStaffUser();
  const { scope, siteId, sites, sampleData, setToast } =
    useTroubleshootingStore();
  const activeSiteId = scope === "site" ? siteId : null;
  const site = sites.find((row) => row.id === activeSiteId);

  // ---- cameras at the site ------------------------------------------------
  const [cameras, setCameras] = React.useState<SiteCamera[] | null>(null);
  const [camerasError, setCamerasError] = React.useState("");
  const [cameraId, setCameraId] = React.useState<number | null>(null);
  React.useEffect(() => {
    setCameraId(null);
    setCamerasError("");
    // Nothing is read for non-staff (admin would answer 403 anyway).
    if (!staff || activeSiteId == null) {
      setCameras(null);
      return;
    }
    let cancelled = false;
    setCameras(null);
    fetchSiteCameras(activeSiteId)
      .then((rows) => {
        if (!cancelled) setCameras(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setCameras([]);
          setCamerasError("This site's cameras could not be loaded.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeSiteId, staff]);

  const cameraOptions = React.useMemo(() => {
    const real = cameras ?? [];
    return sampleData
      ? [
          { id: SAMPLE_CAMERA_ID, camera_name: SAMPLE_PANORAMA.camera_name },
          ...real,
        ]
      : real;
  }, [cameras, sampleData]);
  const isSample = cameraId === SAMPLE_CAMERA_ID;

  // ---- the camera's panorama record --------------------------------------
  const [record, setRecord] = React.useState<PanoramaOutcome | null>(null);
  const [text, setText] = React.useState("");
  const [params, setParams] = React.useState<PanoParams>({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [savedAt, setSavedAt] = React.useState<number | null>(null);

  const loadEditor = React.useCallback((value: string | null) => {
    const next = value ?? "";
    setText(next);
    const parsed = parsePanoramaParameters(next);
    setParams(parsed.ok ? parsed.params : {});
  }, []);

  React.useEffect(() => {
    setRecord(null);
    setSaveError("");
    setSavedAt(null);
    loadEditor("");
    if (cameraId == null) return;
    if (cameraId === SAMPLE_CAMERA_ID) {
      setRecord({ kind: "ok", data: SAMPLE_PANORAMA });
      loadEditor(SAMPLE_PANORAMA.panorama_parameters);
      return;
    }
    let cancelled = false;
    fetchCameraPanorama(cameraId).then((outcome) => {
      if (cancelled) return;
      setRecord(outcome);
      if (outcome.kind === "ok") loadEditor(outcome.data.panorama_parameters);
    });
    return () => {
      cancelled = true;
    };
  }, [cameraId, loadEditor]);

  const current: CameraPanorama | null =
    record?.kind === "ok" ? record.data : null;
  const parsed = React.useMemo(() => parsePanoramaParameters(text), [text]);
  const changed =
    current != null &&
    parsed.ok &&
    parsed.normalized !== normalizePanoramaText(current.panorama_parameters);
  const canSave = changed && !saving;

  const applyParams = (next: PanoParams) => {
    setParams(next);
    setText(serializePanoramaParameters(next));
    setSaveError("");
  };
  const setFlag = (flag: PanoFlag, values: number[] | undefined) => {
    const next = { ...params };
    if (values) next[flag] = values;
    else delete next[flag];
    applyParams(next);
  };
  const setPair = (
    flag: PanoFlag,
    index: 0 | 1,
    value: number | undefined,
    fallback: [number, number]
  ) => {
    if (value === undefined) return setFlag(flag, undefined);
    const pair = [...(params[flag] ?? fallback)];
    pair[index] = value;
    setFlag(flag, pair);
  };
  const setSingle = (flag: PanoFlag) => (value: number | undefined) =>
    setFlag(flag, value === undefined ? undefined : [value]);

  const onTextChange = (raw: string) => {
    setText(raw);
    setSaveError("");
    const result = parsePanoramaParameters(raw);
    if (result.ok) setParams(result.params);
  };

  // ---- the raw frame ------------------------------------------------------
  const [snapshotState, setSnapshotState] = React.useState<
    "idle" | "loading" | "none" | "error"
  >("idle");
  const [snapshotMessage, setSnapshotMessage] = React.useState("");
  const [source, setSource] = React.useState<Source | null>(null);
  const [frame, setFrame] = React.useState<Frame | null>(null);
  /** The image loaded but its pixels can't be read (no CORS on the link). */
  const [tainted, setTainted] = React.useState(false);
  const [snapshotToken, setSnapshotToken] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    setFrame(null);
    setTainted(false);
    setSnapshotMessage("");
    if (cameraId == null) {
      setSource(null);
      setSnapshotState("idle");
      return;
    }
    if (cameraId === SAMPLE_CAMERA_ID) {
      setSource({ kind: "sample" });
      setSnapshotState("idle");
      return;
    }
    if (activeSiteId == null) return;
    let cancelled = false;
    setSource(null);
    setSnapshotState("loading");
    fetchCameraSnapshot(activeSiteId, cameraId).then((outcome) => {
      if (cancelled) return;
      if (outcome.kind === "ok") {
        setSnapshotState("idle");
        setSource({
          kind: "snapshot",
          url: outcome.snapshot.url,
          snapshot: outcome.snapshot,
        });
      } else if (outcome.kind === "none") {
        setSnapshotState("none");
      } else {
        setSnapshotState("error");
        setSnapshotMessage(outcome.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cameraId, activeSiteId, snapshotToken]);

  // Object URLs for local files are revoked when replaced or on unmount.
  React.useEffect(() => {
    const url = source?.kind === "file" ? source.url : null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [source]);

  React.useEffect(() => {
    if (source?.kind !== "sample") return;
    const raster = fisheyeTestPattern(SAMPLE_FRAME_SIZE);
    setFrame({
      raster,
      sourceScale: 1,
      naturalWidth: SAMPLE_FRAME_SIZE,
      naturalHeight: SAMPLE_FRAME_SIZE,
    });
  }, [source]);

  const sampleCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  React.useEffect(() => {
    if (source?.kind === "sample" && frame) {
      drawRaster(sampleCanvasRef.current, frame.raster);
    }
  }, [source, frame]);

  const onImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    try {
      const next = frameFromImage(event.currentTarget);
      if (next) setFrame(next);
      else setTainted(true);
    } catch {
      setTainted(true);
    }
  };

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setFrame(null);
    setTainted(false);
    // Never uploaded: read in the browser only.
    setSource({
      kind: "file",
      url: URL.createObjectURL(file),
      name: file.name,
    });
  };

  // ---- preview ------------------------------------------------------------
  const outputCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const fullLayout = React.useMemo(() => panoramaLayout(params), [params]);
  const [rendered, setRendered] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const hasFlags = Object.keys(params).length > 0;

  React.useEffect(() => {
    setRendered(null);
    if (!frame || !hasFlags || !fullLayout) return;
    const timer = window.setTimeout(() => {
      const result = dewarpFisheye(frame.raster, params, {
        maxWidth: PREVIEW_MAX_WIDTH,
        sourceScale: frame.sourceScale,
      });
      if (!result) return;
      drawRaster(outputCanvasRef.current, result.image);
      setRendered({ width: result.image.width, height: result.image.height });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [frame, params, hasFlags, fullLayout]);

  // ---- save ---------------------------------------------------------------
  const save = async () => {
    if (!canSave || cameraId == null || !parsed.ok) return;
    if (isSample) {
      setToast("Sample camera: nothing was saved.");
      return;
    }
    setSaving(true);
    setSaveError("");
    const outcome = await saveCameraPanorama(cameraId, parsed.normalized);
    setSaving(false);
    if (outcome.kind === "ok") {
      setRecord(outcome);
      loadEditor(outcome.data.panorama_parameters);
      const unsyncedSite = outcome.data.site_id ?? activeSiteId;
      if (unsyncedSite != null) markSiteUnsynced(String(unsyncedSite));
      setSavedAt(Date.now());
      setToast(
        "Panorama parameters saved. They reach the connector on the next Sync."
      );
    } else if (outcome.kind === "unavailable") {
      setSaveError(
        `Saving needs ${outcome.endpoint}; it isn't available in this environment yet.`
      );
    } else {
      setSaveError(outcome.message);
    }
  };

  const copyFlags = async () => {
    const ok = await copyToClipboard(parsed.ok ? parsed.normalized : text);
    setToast(ok ? "Flags copied." : "Could not copy to the clipboard.");
  };

  // ---- render -------------------------------------------------------------
  if (!staff) {
    return <EmptyCard>The fisheye dewarp tool is for Actuate staff.</EmptyCard>;
  }
  if (activeSiteId == null && !sampleData) {
    return (
      <EmptyCard>
        Choose a group and a site above, then pick a fisheye camera to set its
        panorama parameters.
      </EmptyCard>
    );
  }

  const style = hasFlags ? panoStyleOf(params) : null;
  const latBand = params["-la"];
  const lonRange = params["-lo"];
  const centre = params["-c"];
  const centreFallback: [number, number] = frame
    ? [Math.round(frame.naturalWidth / 2), Math.round(frame.naturalHeight / 2)]
    : [0, 0];
  const dewarpValue = normalizePanoramaText(current?.dewarp_parameters ?? null);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      data-testid="fisheye-dewarp-tab"
    >
      <Card testId="fisheye-camera-card">
        <CardHeader
          title="Fisheye camera"
          hint={`Panorama parameters are the fish2pano flags the connector applies to ${
            site?.name ? `a camera at ${site.name}` : "a fisheye camera"
          } before detection. Staff only.`}
        />
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <TextField
            select
            size="small"
            label="Camera"
            value={cameraId == null ? "" : String(cameraId)}
            SelectProps={{ native: true }}
            inputProps={{ "data-testid": "fisheye-camera-select" }}
            onChange={(event) => {
              const nextValue = event.target.value;
              setCameraId(nextValue === "" ? null : Number(nextValue));
            }}
            sx={{
              maxWidth: 420,
              "& .MuiInputBase-root": { ...MULISH, backgroundColor: "#fff" },
            }}
          >
            <option value="">
              {activeSiteId != null && cameras == null
                ? "Loading cameras…"
                : "Choose a camera"}
            </option>
            {cameraOptions.map((camera) => (
              <option key={camera.id} value={String(camera.id)}>
                {camera.id === SAMPLE_CAMERA_ID
                  ? `${camera.camera_name} (sample)`
                  : camera.camera_name}
              </option>
            ))}
          </TextField>
          {camerasError && (
            <Hint sx={{ color: TS_COLORS.red }}>{camerasError}</Hint>
          )}
          {cameras != null && cameras.length === 0 && !camerasError && (
            <Hint>This site has no cameras.</Hint>
          )}
          {cameraId != null && record == null && (
            <Hint>Loading the camera…</Hint>
          )}
          {record && record.kind !== "ok" && (
            <Box
              role="alert"
              sx={{ ...MULISH, fontSize: 14, color: TS_COLORS.red }}
              data-testid="fisheye-record-error"
            >
              {record.kind === "unavailable"
                ? `Panorama parameters need ${record.endpoint}; it isn't available in this environment yet.`
                : record.message}
            </Box>
          )}
          {current && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {isSample && <SampleChip />}
                <SectionLabel>Current panorama parameters</SectionLabel>
                {current.panorama_parameters ? (
                  <Mono>{current.panorama_parameters}</Mono>
                ) : (
                  <Hint>None set: the connector uses the raw frame.</Hint>
                )}
                {current.camera_view && (
                  <Pill tone="grey">View: {current.camera_view}</Pill>
                )}
              </Box>
              {dewarpValue && (
                <Box
                  role="alert"
                  data-testid="fisheye-dewarp-warning"
                  sx={{
                    ...MULISH,
                    fontSize: 14,
                    p: 1.5,
                    borderRadius: "6px",
                    border: `1px solid ${TS_COLORS.amber}55`,
                    backgroundColor: TS_COLORS.amberTint,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <span>
                    <b>Dewarp parameters are set</b> (<Mono>{dewarpValue}</Mono>
                    ) but are not applied by the connector — move them to
                    panorama parameters.
                  </span>
                  <TsButton
                    size="sm"
                    variant="outline"
                    onClick={() => onTextChange(dewarpValue)}
                    data-testid="fisheye-use-dewarp"
                  >
                    Use this as the panorama parameters
                  </TsButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Card>

      {current && (
        <Card testId="fisheye-editor-card">
          <CardHeader
            title="Panorama flags"
            hint="Start from a preset, adjust, then save. Pasting flags into the text box updates the controls."
          />
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {PANORAMA_PRESETS.map((preset) => (
                <TsButton
                  key={preset.value}
                  size="sm"
                  variant={
                    parsed.ok && parsed.normalized === preset.value
                      ? "primary"
                      : "quiet"
                  }
                  title={preset.value}
                  onClick={() => onTextChange(preset.value)}
                >
                  {preset.label}
                </TsButton>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={style}
                onChange={(_, next: "band" | "arc" | null) => {
                  if (next) applyParams(switchPanoStyle(params, next));
                }}
                aria-label="Panorama style"
              >
                <ToggleButton
                  value="band"
                  sx={{ ...MULISH, textTransform: "none" }}
                >
                  Split 360° band
                </ToggleButton>
                <ToggleButton
                  value="arc"
                  sx={{ ...MULISH, textTransform: "none" }}
                >
                  Partial arc
                </ToggleButton>
              </ToggleButtonGroup>
              <Hint>
                Tilt <Mono>-x {FIXED_TILT}</Mono> is fixed (every fisheye in
                production uses it).
              </Hint>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <NumField
                label="Width"
                flag="-w"
                value={params["-w"]?.[0]}
                onCommit={setSingle("-w")}
                testId="fisheye-width"
              />
              {style === "band" && (
                <>
                  <NumField
                    label="Band top"
                    flag="-la"
                    value={latBand?.[0]}
                    onCommit={(v) => setPair("-la", 0, v, [-60, -10])}
                  />
                  <NumField
                    label="Band bottom"
                    flag="-la"
                    value={latBand?.[1]}
                    onCommit={(v) => setPair("-la", 1, v, [-60, -10])}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={params["-2"] !== undefined}
                        onChange={(event) => {
                          const on = event.target.checked;
                          setFlag("-2", on ? [] : undefined);
                        }}
                      />
                    }
                    label={
                      <span style={{ fontFamily: "Mulish", fontSize: 14 }}>
                        Split in two{" "}
                        <span style={{ ...MONO, fontSize: 11 }}>-2</span>
                      </span>
                    }
                  />
                </>
              )}
              {style === "arc" && (
                <>
                  <NumField
                    label="Lens FOV°"
                    flag="-s"
                    value={params["-s"]?.[0]}
                    onCommit={setSingle("-s")}
                  />
                  <NumField
                    label="Longitude from"
                    flag="-lo"
                    value={lonRange?.[0]}
                    onCommit={(v) => setPair("-lo", 0, v, [0, 180])}
                  />
                  <NumField
                    label="Longitude to"
                    flag="-lo"
                    value={lonRange?.[1]}
                    onCommit={(v) => setPair("-lo", 1, v, [0, 180])}
                  />
                </>
              )}
              <NumField
                label="Pan°"
                flag="-z"
                value={params["-z"]?.[0]}
                onCommit={setSingle("-z")}
              />
              <NumField
                label="Radius px"
                flag="-r"
                value={params["-r"]?.[0]}
                onCommit={setSingle("-r")}
              />
              <NumField
                label="Centre x"
                flag="-c"
                value={centre?.[0]}
                onCommit={(v) => setPair("-c", 0, v, centreFallback)}
              />
              <NumField
                label="Centre y"
                flag="-c"
                value={centre?.[1]}
                onCommit={(v) => setPair("-c", 1, v, centreFallback)}
              />
              <NumField
                label="Antialias"
                flag="-a"
                value={params["-a"]?.[0]}
                onCommit={setSingle("-a")}
              />
            </Box>

            <TextField
              label="fish2pano flags"
              value={text}
              onChange={(event) => {
                const nextValue = event.target.value;
                onTextChange(nextValue);
              }}
              error={!parsed.ok}
              helperText={
                parsed.ok
                  ? parsed.normalized
                    ? " "
                    : "Empty: saving clears the panorama parameters."
                  : parsed.error
              }
              FormHelperTextProps={
                { "data-testid": "fisheye-flags-help" } as never
              }
              inputProps={{ "data-testid": "fisheye-flags", spellCheck: false }}
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  ...MONO,
                  fontSize: 14,
                  backgroundColor: "#fff",
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TsButton
                onClick={() => void save()}
                disabled={!canSave}
                data-testid="fisheye-save"
              >
                {saving ? "Saving…" : "Save panorama parameters"}
              </TsButton>
              <TsButton
                variant="quiet"
                onClick={() => void copyFlags()}
                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                disabled={!text.trim()}
              >
                Copy flags
              </TsButton>
              {changed && (
                <TsButton
                  variant="ghost"
                  onClick={() => loadEditor(current.panorama_parameters)}
                >
                  Revert
                </TsButton>
              )}
              <Hint sx={{ flex: "1 1 260px" }}>
                {savedAt
                  ? "Saved. The site is now unsynced: it reaches the connector on the next Sync (armed sites also need the restart Sync offers)."
                  : "Saving marks the site unsynced. Nothing reaches the connector until the next Sync."}
              </Hint>
            </Box>
            {saveError && (
              <Box
                role="alert"
                data-testid="fisheye-save-error"
                sx={{ ...MULISH, fontSize: 14, color: TS_COLORS.red }}
              >
                {saveError}
              </Box>
            )}
          </Box>
        </Card>
      )}

      {current && (
        <Card testId="fisheye-preview-card">
          <CardHeader
            title="Preview"
            hint={PREVIEW_LABEL}
            right={
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onPickFile}
                  data-testid="fisheye-file-input"
                />
                <TsButton
                  size="sm"
                  variant="quiet"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Load an image file…
                </TsButton>
                {activeSiteId != null && !isSample && (
                  <TsButton
                    size="sm"
                    variant="quiet"
                    onClick={() => setSnapshotToken((n) => n + 1)}
                  >
                    Reload test frame
                  </TsButton>
                )}
              </>
            }
          />
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Hint>
              {source?.kind === "snapshot"
                ? describeSnapshotAge(source.snapshot)
                : source?.kind === "file"
                ? `Local file ${source.name} (not uploaded).`
                : source?.kind === "sample"
                ? "Generated test pattern: coloured 30° sectors and a ring every 10° of latitude."
                : snapshotState === "loading"
                ? "Loading the latest camera test…"
                : snapshotState === "error"
                ? `${snapshotMessage} Load an image file to preview instead.`
                : snapshotState === "none"
                ? "Run a camera test with a snapshot to get a frame, or load an image file."
                : ""}
            </Hint>
            {source?.kind === "snapshot" &&
              source.snapshot.expiresAt &&
              Date.parse(source.snapshot.expiresAt) < Date.now() && (
                <Hint sx={{ color: TS_COLORS.amber }}>
                  This frame&rsquo;s link has expired; use Reload test frame.
                </Hint>
              )}
            {tainted && (
              <Hint sx={{ color: TS_COLORS.amber }}>
                The frame shows, but the browser won&rsquo;t let this page read
                its pixels (the link has no CORS headers), so it can&rsquo;t be
                dewarped here. Save the image and use Load an image file.
              </Hint>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(180px, 280px) 1fr",
                },
                gap: 2,
                alignItems: "start",
              }}
            >
              <Box>
                <SectionLabel>Raw frame</SectionLabel>
                {source?.kind === "sample" ? (
                  <canvas
                    ref={sampleCanvasRef}
                    style={{ width: "100%", display: "block", borderRadius: 4 }}
                  />
                ) : source ? (
                  <img
                    // A remount on fallback, so the second load drops CORS.
                    key={`${source.url}|${tainted ? "plain" : "cors"}`}
                    src={source.url}
                    alt="Raw fisheye frame"
                    crossOrigin={
                      source.kind === "snapshot" && !tainted
                        ? "anonymous"
                        : undefined
                    }
                    onLoad={tainted ? undefined : onImageLoad}
                    onError={() => {
                      if (source.kind === "snapshot" && !tainted)
                        setTainted(true);
                    }}
                    style={{ width: "100%", display: "block", borderRadius: 4 }}
                  />
                ) : (
                  <Hint>No frame yet.</Hint>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <SectionLabel>Panorama</SectionLabel>
                <Box
                  sx={{ ...MULISH, fontSize: 13, color: TS_COLORS.ink2, mb: 1 }}
                  data-testid="fisheye-output-size"
                >
                  {!hasFlags
                    ? "No flags: choose a preset or a style to preview."
                    : fullLayout
                    ? `Connector output ${fullLayout.outWidth} × ${
                        fullLayout.outHeight
                      } px${
                        rendered && rendered.width !== fullLayout.outWidth
                          ? ` (preview drawn at ${rendered.width} × ${rendered.height})`
                          : ""
                      }`
                    : "These flags describe no image (the band must be between 0° and 180° tall)."}
                  {!parsed.ok && hasFlags && " · showing the last valid flags"}
                </Box>
                <canvas
                  ref={outputCanvasRef}
                  data-testid="fisheye-output"
                  style={{
                    width: "100%",
                    display: frame && hasFlags && fullLayout ? "block" : "none",
                    background: "#111827",
                    borderRadius: 4,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      )}
    </Box>
  );
};
